import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, Calendar, Edit2, Search, RefreshCw, Trash2, Mic, MicOff, LogOut, CheckSquare, Square, Plus, ArrowUp, ArrowDown, Copy } from 'lucide-react';

// API Client — use relative path when served via Flask/Railway, absolute when in local dev
const API_BASE = window.location.port === '3000'
  ? 'http://localhost:5000/api'
  : '/api';

// Base headers — includes ngrok bypass so API calls work through ngrok free tier
const BASE_HEADERS = { 'ngrok-skip-browser-warning': 'true' };

// Unauthorized handler — set by the main component so API calls can trigger logout
let _onUnauthorized = null;

const api = {
  setUnauthorizedHandler(fn) {
    _onUnauthorized = fn;
  },

  async _fetch(url, options = {}) {
    const res = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: { ...BASE_HEADERS, ...(options.headers || {}) }
    });
    if (res.status === 401) {
      if (_onUnauthorized) _onUnauthorized();
      throw new Error('Unauthorized');
    }
    return res;
  },

  async getAccounts() {
    const res = await this._fetch(`${API_BASE}/accounts`);
    return res.json();
  },

  async getEntries(accountId = null, search = '') {
    const params = new URLSearchParams();
    if (accountId) params.append('account_id', accountId);
    if (search) params.append('search', search);
    const res = await this._fetch(`${API_BASE}/entries?${params}`);
    return res.json();
  },

  async createEntry(data) {
    const res = await this._fetch(`${API_BASE}/entries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },

  async upsertEntry(data) {
    const res = await this._fetch(`${API_BASE}/entries/upsert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },

  async updateEntry(id, data) {
    const res = await this._fetch(`${API_BASE}/entries/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },

  async deleteEntry(id) {
    await this._fetch(`${API_BASE}/entries/${id}`, { method: 'DELETE' });
  },

  async getPendingChanges(accountId = null) {
    const params = new URLSearchParams();
    if (accountId) params.append('account_id', accountId);
    const res = await this._fetch(`${API_BASE}/changes/pending?${params}`);
    return res.json();
  },

  async triggerSync(daysBack = 2) {
    const res = await this._fetch(`${API_BASE}/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ days_back: daysBack })
    });
    return res.json();
  },

  async updateAccount(id, data) {
    const res = await this._fetch(`${API_BASE}/accounts/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },

  async discoverAccounts(mccId) {
    const res = await this._fetch(`${API_BASE}/accounts/discover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mcc_id: mccId })
    });
    return res.json();
  },

  async deleteAccount(id) {
    await this._fetch(`${API_BASE}/accounts/${id}`, { method: 'DELETE' });
  },

  async mergeAccounts(sourceId, targetId) {
    const res = await this._fetch(`${API_BASE}/accounts/${sourceId}/merge/${targetId}`, { method: 'POST' });
    return res.json();
  },

  async createAccount(data) {
    const res = await this._fetch(`${API_BASE}/accounts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },

  async polish(text, platform) {
    const res = await this._fetch(`${API_BASE}/polish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, platform })
    });
    return res.json();
  }
};

// Debounce utility
function useDebounce(callback, delay) {
  const timeoutRef = React.useRef(null);

  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
}

// Convert spoken punctuation words to symbols and fix capitalisation
function formatTranscript(text) {
  let t = text
    .replace(/\bperiod\b/gi, '.')
    .replace(/\bcomma\b/gi, ',')
    .replace(/\bquestion mark\b/gi, '?')
    .replace(/\bexclamation point\b/gi, '!')
    .replace(/\b(new line|new paragraph)\b/gi, '\n');

  // Remove any space that crept in before punctuation: "groups ." -> "groups."
  t = t.replace(/\s+([.,!?])/g, '$1');

  // Capitalise after '. ' and at the very start
  t = t.replace(/(^\s*|[.!?]\s+)([a-z])/g, (_, prefix, letter) => prefix + letter.toUpperCase());

  return t.trim();
}

// Speech recognition hook — shows live interim text, fires onComplete with full transcript when done
function useSpeechRecognition({ onComplete }) {
  const recognitionRef = React.useRef(null);
  const accumulatedRef = React.useRef('');
  const onCompleteRef = React.useRef(onComplete);
  const [isListening, setIsListening] = React.useState(false);
  const [interimText, setInterimText] = React.useState('');

  React.useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  const supported = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  function start() {
    if (!supported || isListening) return;
    accumulatedRef.current = '';

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let finalChunk = '';
      let interim = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalChunk += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }

      if (finalChunk.trim()) {
        accumulatedRef.current = accumulatedRef.current
          ? accumulatedRef.current + ' ' + finalChunk.trim()
          : finalChunk.trim();
        setInterimText('');
      } else {
        setInterimText(interim);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimText('');
      const text = accumulatedRef.current.trim();
      accumulatedRef.current = '';
      if (text) onCompleteRef.current(text);
    };

    recognition.onerror = () => {
      setIsListening(false);
      setInterimText('');
      accumulatedRef.current = '';
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }

  function stop() {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
  }

  return { isListening, interimText, start, stop, supported };
}

// Render "Google" with per-letter brand colors
function GoogleLabel() {
  const letters = [
    ['G', '#4285F4'], ['o', '#EA4335'], ['o', '#FBBC05'],
    ['g', '#4285F4'], ['l', '#34A853'], ['e', '#EA4335'],
  ];
  return (
    <span style={{ fontWeight: '700' }}>
      {letters.map(([l, c], i) => <span key={i} style={{ color: c }}>{l}</span>)}
    </span>
  );
}


// Get today's date in local time as YYYY-MM-DD
function getLocalToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const PLATFORM_CHANNELS = [
  { id: 'general', label: 'General', color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' },
  { id: 'meta_ads', label: 'Meta', color: '#7c3aed', bg: '#f5f3ff', border: '#c4b5fd' },
  { id: 'google_ads', label: 'Google', color: '#4285F4', bg: '#f0f7ff', border: '#93c5fd' },
];

function channelTypeLabel(type) {
  const map = {
    'SEARCH': 'Search', 'DISPLAY': 'Display', 'PERFORMANCE_MAX': 'PMax',
    'VIDEO': 'YouTube', 'SHOPPING': 'Shopping', 'MULTI_CHANNEL': 'Multi',
    'LOCAL': 'Local', 'SMART': 'Smart', 'DISCOVERY': 'Demand Gen', 'DEMAND_GEN': 'Demand Gen',
  };
  return map[type] || type;
}

function CampaignPicker({ platform, campaigns, onSelect, onClose }) {
  const ref = React.useRef(null);

  React.useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    function handleKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
  }, [onClose]);

  const platformLabel = platform === 'google_ads' ? 'Google'
                      : platform === 'meta_ads' ? 'Meta' : 'General';

  return (
    <div ref={ref} style={{
      position: 'absolute', top: '100%', left: 0, marginTop: '0.375rem',
      background: 'white', borderRadius: '10px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.16)', border: '1px solid #e5e7eb',
      minWidth: '240px', maxHeight: '300px', overflowY: 'auto',
      zIndex: 100, padding: '0.375rem'
    }}>
      {campaigns.length > 0 && (
        <>
          <div style={{
            fontSize: '0.675rem', fontWeight: '700', color: '#9ca3af',
            textTransform: 'uppercase', letterSpacing: '0.06em',
            padding: '0.375rem 0.5rem 0.25rem'
          }}>
            Specify campaign
          </div>
          {campaigns.map(c => (
            <button key={c.id} onClick={() => onSelect(c.name)} style={{
              width: '100%', padding: '0.4rem 0.75rem', background: 'transparent',
              border: 'none', borderRadius: '6px', cursor: 'pointer',
              textAlign: 'left', fontSize: '0.8125rem', color: '#374151',
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              transition: 'background 0.1s'
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ flex: 1 }}>{c.name}</span>
              {platform === 'google_ads' && <span style={{
                fontSize: '0.675rem', color: '#9ca3af', background: '#f3f4f6',
                padding: '0.125rem 0.375rem', borderRadius: '4px'
              }}>{channelTypeLabel(c.type)}</span>}
            </button>
          ))}
        </>
      )}

      {campaigns.length === 0 && (
        <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#9ca3af' }}>
          No campaigns loaded — run Sync to fetch
        </div>
      )}
    </div>
  );
}

// Textarea with colored heading overlay — keeps native editing (undo, paste, selection)
// while rendering platform headings in their brand colors behind the transparent text.
function ColoredNoteEditor({ value, onChange, placeholder, minHeight, border, editorRef }) {
  const internalRef = React.useRef(null);
  const ref = editorRef || internalRef;

  React.useEffect(() => {
    if (!ref.current) return;
    ref.current.style.height = 'auto';
    ref.current.style.height = ref.current.scrollHeight + 'px';
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const googleColors = [
    ['G', '#4285F4'], ['o', '#EA4335'], ['o', '#FBBC05'],
    ['g', '#4285F4'], ['l', '#34A853'], ['e', '#EA4335'],
  ];

  function renderGoogleInline(suffix) {
    return (
      <span style={{ fontWeight: 700 }}>
        {googleColors.map(([l, c], j) => <span key={j} style={{ color: c }}>{l}</span>)}
        <span style={{ color: '#5f6368' }}>{suffix}</span>
      </span>
    );
  }

  function colorize(text) {
    if (!text) return null;
    const lines = text.split('\n');
    const parts = [];
    lines.forEach((line, i) => {
      if (i > 0) parts.push('\n');

      const headingMatch = line.match(/^(Google Ads|Google|Meta|General)(\s*>\s*.+)?$/);
      if (headingMatch) {
        if (headingMatch[1] === 'Google' || headingMatch[1] === 'Google Ads') {
          const suffix = headingMatch[2] || '';
          parts.push(<React.Fragment key={i}>{renderGoogleInline(suffix)}</React.Fragment>);
        } else if (headingMatch[1] === 'Meta') {
          const suffix = headingMatch[2] || '';
          parts.push(
            <span key={i} style={{ fontWeight: 700 }}>
              <span style={{ color: '#7c3aed' }}>Meta</span>
              <span style={{ color: '#5f6368' }}>{suffix}</span>
            </span>
          );
        } else {
          parts.push(<span key={i} style={{ color: '#6b7280', fontWeight: 700 }}>{line}</span>);
        }
        return;
      }
      if (line.startsWith('[Google Ads]')) {
        parts.push(
          <React.Fragment key={i}>
            <span style={{ fontWeight: 700 }}>
              <span style={{ color: '#5f6368' }}>[</span>
              {googleColors.map(([l, c], j) => <span key={j} style={{ color: c }}>{l}</span>)}
              <span style={{ color: '#5f6368' }}> Ads]</span>
            </span>
            {line.slice('[Google Ads]'.length)}
          </React.Fragment>
        );
        return;
      }
      if (line.startsWith('[Meta]')) {
        parts.push(
          <React.Fragment key={i}>
            <span style={{ color: '#7c3aed', fontWeight: 700 }}>[Meta]</span>
            {line.slice('[Meta]'.length)}
          </React.Fragment>
        );
        return;
      }
      parts.push(line);
    });
    return parts;
  }

  return (
    <div style={{
      position: 'relative',
      borderRadius: '8px',
      border: border || '1px solid #e5e7eb',
      background: '#fafbfc',
    }}>
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          minHeight: minHeight || '120px',
          padding: '1rem',
          border: 'none',
          borderRadius: '8px',
          fontSize: '0.9375rem',
          fontFamily: 'inherit',
          resize: 'none',
          overflow: 'hidden',
          lineHeight: '1.6',
          color: 'transparent',
          caretColor: '#1a1a1a',
          background: 'transparent',
          boxSizing: 'border-box',
          position: 'relative',
          zIndex: 1,
          outline: 'none',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          padding: '1rem',
          fontSize: '0.9375rem',
          fontFamily: 'inherit',
          lineHeight: '1.6',
          color: '#1a1a1a',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'break-word',
          wordWrap: 'break-word',
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 0,
          boxSizing: 'border-box',
        }}
      >
        {value ? colorize(value) : (
          <span style={{ color: '#9ca3af' }}>{placeholder}</span>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// LOGIN SCREEN
// =============================================================================

function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      if (res.ok) {
        onLogin();
      } else {
        const data = await res.json();
        setError(data.error || 'Invalid credentials');
      }
    } catch {
      setError('Connection error — is the server running?');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '"Graphik", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '2.5rem',
        width: '100%',
        maxWidth: '380px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        border: '1px solid rgba(0,0,0,0.06)'
      }}>
        {/* Logo / Title */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: '52px',
            height: '52px',
            background: 'linear-gradient(135deg, #2c3e50 0%, #1a252f 100%)',
            borderRadius: '12px',
            margin: '0 auto 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <span style={{ fontSize: '1.5rem' }}>📓</span>
          </div>
          <h1 style={{
            margin: '0 0 0.25rem',
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#1a1a1a',
            letterSpacing: '-0.02em'
          }}>
            Orchestra
          </h1>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
            Sign in to continue
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.375rem' }}>
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
              style={{
                width: '100%',
                padding: '0.625rem 0.875rem',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '0.9375rem',
                boxSizing: 'border-box',
                outline: 'none',
                transition: 'border-color 0.15s'
              }}
              onFocus={e => e.target.style.borderColor = '#667eea'}
              onBlur={e => e.target.style.borderColor = '#d1d5db'}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.375rem' }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              style={{
                width: '100%',
                padding: '0.625rem 0.875rem',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '0.9375rem',
                boxSizing: 'border-box',
                outline: 'none',
                transition: 'border-color 0.15s'
              }}
              onFocus={e => e.target.style.borderColor = '#667eea'}
              onBlur={e => e.target.style.borderColor = '#d1d5db'}
            />
          </div>

          {error && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.625rem 0.875rem',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#dc2626',
              fontSize: '0.875rem'
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: isLoading ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9375rem',
              fontWeight: '600',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'opacity 0.15s'
            }}
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN APP
// =============================================================================

export default function AdsJournal() {
  // null = checking session, false = not logged in, true = logged in
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [entries, setEntries] = useState([]);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingChanges, setPendingChanges] = useState([]);
  const [activeTab, setActiveTab] = useState('journal');
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [addingManualAccount, setAddingManualAccount] = useState(false);
  const [manualAccountName, setManualAccountName] = useState('');
  const [saveStatus, setSaveStatus] = useState('saved');
  const [campaignPicker, setCampaignPicker] = useState(null);

  // Wire up the 401 handler so any API call can redirect to login
  useEffect(() => {
    api.setUnauthorizedHandler(() => setIsAuthenticated(false));
  }, []);

  // Check session on mount
  useEffect(() => {
    fetch(`${API_BASE}/auth/me`, { credentials: 'include', headers: BASE_HEADERS })
      .then(res => {
        setIsAuthenticated(res.ok);
      })
      .catch(() => setIsAuthenticated(false));
  }, []);

  // Load accounts once authenticated
  useEffect(() => {
    if (isAuthenticated === true) {
      loadAccounts();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  function campaignsForPlatform(channelId) {
    const acct = accounts.find(a => a.id === selectedAccount);
    if (!acct) return [];
    if (channelId === 'google_ads') return acct.campaigns || [];
    if (channelId === 'meta_ads') return acct.meta_campaigns || [];
    return [];
  }

  function handlePlatformClick(channelId) {
    insertHeading(channelId);
    if (channelId !== 'general' && campaignsForPlatform(channelId).length > 0) {
      setCampaignPicker(channelId);
    }
  }

  function insertHeading(platform) {
    const label = platform === 'google_ads' ? 'Google'
                : platform === 'meta_ads' ? 'Meta'
                : 'General';

    setNewEntry(prev => {
      const note = prev.note;
      let separator = '';
      if (note.length > 0) {
        if (!note.endsWith('\n\n')) {
          separator = note.endsWith('\n') ? '\n' : '\n\n';
        }
      }
      const updated = note + separator + label + '\n';
      debouncedAutoSave(selectedAccount, prev.date, updated);
      return { ...prev, note: updated };
    });

    setTimeout(() => {
      if (newEntryRef.current) {
        newEntryRef.current.focus();
        const len = newEntryRef.current.value.length;
        newEntryRef.current.selectionStart = len;
        newEntryRef.current.selectionEnd = len;
      }
    }, 0);
  }

  function refineCampaign(campaignName) {
    const label = campaignPicker === 'google_ads' ? 'Google' : 'Meta';
    setNewEntry(prev => {
      const bare = label + '\n';
      const lastIdx = prev.note.lastIndexOf(bare);
      if (lastIdx === -1) return prev;
      const updated = prev.note.slice(0, lastIdx) + `${label} > ${campaignName}\n` + prev.note.slice(lastIdx + bare.length);
      debouncedAutoSave(selectedAccount, prev.date, updated);
      return { ...prev, note: updated };
    });
    setCampaignPicker(null);
    setTimeout(() => {
      if (newEntryRef.current) {
        newEntryRef.current.focus();
        const len = newEntryRef.current.value.length;
        newEntryRef.current.selectionStart = len;
        newEntryRef.current.selectionEnd = len;
      }
    }, 0);
  }

  // New entry state
  const [newEntry, setNewEntry] = useState({
    date: getLocalToday(),
    note: ''
  });

  const newEntryRef = React.useRef(null);

  // Load entries when account changes
  useEffect(() => {
    if (selectedAccount) {
      const today = getLocalToday();
      setNewEntry({ date: today, note: '' });
      loadEntries().then(data => {
        const existing = data.find(e => e.entry_date === today);
        if (existing) setNewEntry(prev => ({ ...prev, note: existing.manual_note || '' }));
      });
      loadPendingChanges();
    }
  }, [selectedAccount, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // When date picker changes, load existing entry for that date
  function handleDateChange(newDate) {
    const existing = entries.find(e => e.entry_date === newDate);
    setNewEntry({ date: newDate, note: existing ? (existing.manual_note || '') : '' });
  }

  function sortAccounts(list) {
    return [...list].sort((a, b) =>
      a.account_name.localeCompare(b.account_name, undefined, { sensitivity: 'base' })
    );
  }

  async function loadAccounts() {
    try {
      const data = await api.getAccounts();
      const sorted = sortAccounts(data);
      setAccounts(sorted);
      if (sorted.length > 0 && !selectedAccount) {
        setSelectedAccount(sorted[0].id);
      }
    } catch {}
  }

  async function loadEntries() {
    try {
      const data = await api.getEntries(selectedAccount, searchQuery);
      setEntries(data);
      return data;
    } catch {
      return [];
    }
  }

  async function loadPendingChanges() {
    try {
      const data = await api.getPendingChanges(selectedAccount);
      setPendingChanges(data);
    } catch {}
  }

  const debouncedAutoSave = useDebounce(async (accountId, date, note) => {
    setSaveStatus('saving');
    await api.upsertEntry({
      account_id: accountId,
      entry_date: date,
      manual_note: note
    });
    // Refresh entry list (but don't overwrite the active editor)
    try {
      const data = await api.getEntries(accountId, searchQuery);
      setEntries(data);
    } catch {}
    await loadPendingChanges();
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus(''), 2000);
  }, 1000);

  const debouncedUpdate = useDebounce(async (id, note) => {
    setSaveStatus('saving');
    await api.updateEntry(id, { manual_note: note });
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus(''), 2000);
  }, 1000);

  async function handleDiscover() {
    setIsDiscovering(true);
    try {
      await api.discoverAccounts('7517452201');
      await loadAccounts();
    } catch {}
    setIsDiscovering(false);
  }

  async function handleAddManualAccount() {
    if (!manualAccountName.trim()) return;
    try {
      const account = await api.createAccount({
        platform: 'google_ads',
        account_id: `manual-${Date.now()}`,
        account_name: manualAccountName.trim()
      });
      await loadAccounts();
      setSelectedAccount(account.id);
      setManualAccountName('');
      setAddingManualAccount(false);
    } catch {}
  }

  async function handleSync(daysBack = 2) {
    setIsSyncing(true);
    try {
      await api.triggerSync(daysBack);
      await loadAccounts();
      await loadEntries();
      await loadPendingChanges();
    } catch {}
    setIsSyncing(false);
  }

  async function handleDeleteEntry(id) {
    if (!window.confirm('Delete this entry? Changes will be unlogged but not deleted.')) return;
    await api.deleteEntry(id);
    await loadEntries();
  }

  async function doLogout() {
    await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include', headers: BASE_HEADERS });
    setIsAuthenticated(false);
    setAccounts([]);
    setEntries([]);
    setSelectedAccount(null);
  }

  // Show loading spinner while checking session
  if (isAuthenticated === null) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"Graphik", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        color: '#6b7280'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (isAuthenticated === false) {
    return <LoginScreen onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      fontFamily: '"Graphik", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      color: '#1a1a1a'
    }}>

      {/* Header */}
      <header style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #2d1b69 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        padding: '1.5rem 2rem',
        boxShadow: '0 4px 24px rgba(0,0,0,0.15)'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{
              margin: '0 0 0.25rem 0',
              fontSize: '1.75rem',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #fff 0%, #cbd5e0 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.02em'
            }}>
              Orchestra
            </h1>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem', textAlign: 'center' }}>
              by Story Pro
            </p>
          </div>

          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <button
              onClick={() => handleSync(2)}
              disabled={isSyncing}
              style={{
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'white',
                cursor: isSyncing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                opacity: isSyncing ? 0.6 : 1
              }}
            >
              <RefreshCw size={16} style={{ animation: isSyncing ? 'spin 1s linear infinite' : 'none' }} />
              {isSyncing ? 'Syncing...' : 'Sync Now'}
            </button>

<div style={{
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'white',
              fontSize: '0.875rem'
            }}>
              <Calendar size={16} />
              <span>{new Date().toLocaleDateString()}</span>
            </div>

            <button
              onClick={doLogout}
              title="Sign out"
              style={{
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem'
              }}
            >
              <LogOut size={16} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem', display: 'grid', gridTemplateColumns: '280px 1fr', gap: '2rem' }}>

        {/* Sidebar */}
        <aside>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.05)'
          }}>
            <h3 style={{
              margin: '0 0 1rem 0',
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: '#6b7280',
              fontWeight: '600'
            }}>
              Ad Accounts
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.375rem' }}>
              {accounts.map(account => (
                <div
                  key={account.id}
                  onClick={() => setSelectedAccount(account.id)}
                  style={{
                    background: selectedAccount === account.id ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                    color: selectedAccount === account.id ? 'white' : '#374151',
                    border: selectedAccount === account.id ? 'none' : '1px solid #e5e7eb',
                    padding: '0.4rem 0.5rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s ease',
                    fontWeight: selectedAccount === account.id ? '600' : '500',
                    fontSize: '0.75rem',
                    userSelect: 'text',
                    minWidth: 0
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    {account.is_spending === false && (
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: selectedAccount === account.id ? 'rgba(255,255,255,0.8)' : '#ef4444',
                        flexShrink: 0
                      }}></div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '600', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{account.account_name}</div>
                      {account.account_id.startsWith('manual-')
                        ? <div style={{ fontSize: '0.625rem', fontWeight: '600', color: selectedAccount === account.id ? 'rgba(255,255,255,0.8)' : '#f59e0b' }}>Pending access</div>
                        : <div style={{ fontSize: '0.675rem', opacity: 0.85, fontWeight: '500' }}>{account.account_id.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}</div>
                      }
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Discover Accounts Button */}
            <button
              onClick={handleDiscover}
              disabled={isDiscovering}
              style={{
                width: '100%',
                marginTop: '0.75rem',
                padding: '0.5rem',
                background: 'transparent',
                border: '1px dashed #d1d5db',
                borderRadius: '8px',
                color: '#6b7280',
                fontSize: '0.8125rem',
                cursor: isDiscovering ? 'not-allowed' : 'pointer',
                opacity: isDiscovering ? 0.6 : 1
              }}
            >
              {isDiscovering ? 'Discovering...' : '+ Discover Accounts'}
            </button>

            {/* Add Manual Account */}
            {addingManualAccount ? (
              <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                <input
                  autoFocus
                  value={manualAccountName}
                  onChange={e => setManualAccountName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddManualAccount(); if (e.key === 'Escape') { setAddingManualAccount(false); setManualAccountName(''); } }}
                  placeholder="Account name..."
                  style={{
                    width: '100%', padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db', borderRadius: '6px',
                    fontSize: '0.8125rem', boxSizing: 'border-box'
                  }}
                />
                <div style={{ display: 'flex', gap: '0.375rem' }}>
                  <button onClick={handleAddManualAccount} style={{
                    flex: 1, padding: '0.4rem', background: '#10b981', color: 'white',
                    border: 'none', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer'
                  }}>Add</button>
                  <button onClick={() => { setAddingManualAccount(false); setManualAccountName(''); }} style={{
                    flex: 1, padding: '0.4rem', background: '#f3f4f6', color: '#6b7280',
                    border: 'none', borderRadius: '6px', fontSize: '0.8rem', cursor: 'pointer'
                  }}>Cancel</button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setAddingManualAccount(true)}
                style={{
                  width: '100%', marginTop: '0.375rem', padding: '0.5rem',
                  background: 'transparent', border: '1px dashed #d1d5db',
                  borderRadius: '8px', color: '#6b7280', fontSize: '0.8125rem', cursor: 'pointer'
                }}
              >
                + Add Manually
              </button>
            )}

            {/* Search Bar */}
            <div style={{ position: 'relative', marginTop: '0.75rem' }}>
              <Search size={14} style={{ position: 'absolute', left: '0.625rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search entries..."
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem 0.5rem 2rem',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  fontSize: '0.8125rem',
                  background: '#f9fafb',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

        </aside>

        {/* Main Area */}
        <main>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem' }}>
            {[['journal', 'Journal'], ['hub', 'Account Hub']].map(([id, label]) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                style={{
                  padding: '0.5rem 1.25rem',
                  borderRadius: '8px',
                  border: 'none',
                  background: activeTab === id ? 'white' : 'rgba(255,255,255,0.5)',
                  color: activeTab === id ? '#1a1a1a' : '#6b7280',
                  fontWeight: activeTab === id ? '700' : '500',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  boxShadow: activeTab === id ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >{label}</button>
            ))}
          </div>

          {activeTab === 'hub' && (
            <AccountHub
              account={accounts.find(a => a.id === selectedAccount)}
              onUpdate={async (field, value) => {
                if (!selectedAccount) return;
                if (field === '_refresh') { await loadAccounts(); return; }
                const updated = await api.updateAccount(selectedAccount, { [field]: value });
                setAccounts(prev => sortAccounts(prev.map(a => a.id === selectedAccount ? updated : a)));
              }}
            />
          )}

          {activeTab === 'journal' && <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 260px', gap: '1.25rem', alignItems: 'start' }}>
          <div>
          {/* New Entry Card */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            border: '2px solid #667eea'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#1a1a1a' }}>
                {newEntry.date === getLocalToday() ? "Today's Entry" : `Entry for ${new Date(newEntry.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
              </h2>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                {saveStatus === 'saving' && <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Saving...</span>}
                {saveStatus === 'saved' && <span style={{ fontSize: '0.75rem', color: '#10b981' }}>Auto-saved</span>}
                <input
                  type="date"
                  value={newEntry.date}
                  onChange={(e) => handleDateChange(e.target.value)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb',
                    fontSize: '0.875rem',
                    color: '#374151',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </div>

            <ColoredNoteEditor
              editorRef={newEntryRef}
              value={newEntry.note}
              onChange={(val) => {
                setNewEntry(prev => ({ ...prev, note: val }));
                debouncedAutoSave(selectedAccount, newEntry.date, val);
              }}
              placeholder="Start typing to auto-save..."
            />

            {/* Platform heading buttons with campaign picker */}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.625rem', position: 'relative' }}>
              {PLATFORM_CHANNELS.map(ch => (
                <button
                  key={ch.id}
                  onClick={() => handlePlatformClick(ch.id)}
                  title={`Insert ${ch.label} heading`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.4rem 0.875rem',
                    borderRadius: '999px',
                    border: `1px solid ${campaignPicker === ch.id ? ch.color : ch.border}`,
                    background: campaignPicker === ch.id ? ch.bg : 'white',
                    color: ch.color,
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                    transition: 'all 0.15s ease',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {ch.id === 'google_ads' ? <GoogleLabel /> : ch.label}
                </button>
              ))}

              {campaignPicker && (
                <CampaignPicker
                  platform={campaignPicker}
                  campaigns={campaignsForPlatform(campaignPicker)}
                  onSelect={refineCampaign}
                  onClose={() => setCampaignPicker(null)}
                />
              )}
            </div>

            {/* Pending changes preview */}
            {pendingChanges.length > 0 && (
              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                borderRadius: '8px',
                border: '1px solid #bae6fd'
              }}>
                <div style={{ fontSize: '0.8125rem', color: '#0369a1', fontWeight: '600', marginBottom: '0.5rem' }}>
                  ✨ Pending changes (will be auto-logged):
                </div>
                <div style={{ fontSize: '0.8125rem', color: '#075985' }}>
                  {pendingChanges.length} change{pendingChanges.length !== 1 ? 's' : ''} detected for this account
                </div>
              </div>
            )}
          </div>

          {/* Journal Entries — grouped by date, most recent first */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {entries.filter(e => e.entry_date !== newEntry.date).length === 0 ? (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '3rem',
                textAlign: 'center',
                color: '#6b7280'
              }}>
                <p style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>No previous entries</p>
                <p style={{ fontSize: '0.875rem' }}>Start typing above — entries auto-save and appear here</p>
              </div>
            ) : (() => {
              // Group entries by date
              const grouped = {};
              const dateOrder = [];
              for (const entry of entries) {
                if (entry.entry_date === newEntry.date) continue; // skip the active editor date
                if (!grouped[entry.entry_date]) {
                  grouped[entry.entry_date] = [];
                  dateOrder.push(entry.entry_date);
                }
                grouped[entry.entry_date].push(entry);
              }
              // dateOrder is already sorted desc from backend
              return dateOrder.map(date => (
                <div key={date}>
                  <div style={{
                    fontSize: '0.8rem',
                    fontWeight: '700',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '0.5rem',
                    marginTop: '0.5rem',
                    paddingLeft: '0.25rem'
                  }}>
                    {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {grouped[date].map(entry => (
                      <EntryCard
                        key={entry.id}
                        entry={entry}
                        expandedEntry={expandedEntry}
                        setExpandedEntry={setExpandedEntry}
                        onUpdate={debouncedUpdate}
                        onDelete={handleDeleteEntry}
                        saveStatus={saveStatus}
                        googleCampaigns={accounts.find(a => a.id === selectedAccount)?.campaigns || []}
                        metaCampaigns={accounts.find(a => a.id === selectedAccount)?.meta_campaigns || []}
                      />
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
          </div>
          {/* Right sidebar: Links + Test Ideas */}
          <div style={{ position: 'sticky', top: '2rem' }}>
            <AccountLinks
              account={accounts.find(a => a.id === selectedAccount)}
              onUpdate={async (field, value) => {
                if (!selectedAccount) return;
                const updated = await api.updateAccount(selectedAccount, { [field]: value });
                setAccounts(prev => sortAccounts(prev.map(a => a.id === selectedAccount ? updated : a)));
              }}
            />
            <TestIdeas
              ideas={accounts.find(a => a.id === selectedAccount)?.test_ideas}
              onUpdate={async (field, value) => {
                if (!selectedAccount) return;
                const updated = await api.updateAccount(selectedAccount, { [field]: value });
                setAccounts(prev => sortAccounts(prev.map(a => a.id === selectedAccount ? updated : a)));
              }}
            />
          </div>
          </div>
          </>}
        </main>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes mic-pulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(239,68,68,0.15); }
          50% { box-shadow: 0 0 0 8px rgba(239,68,68,0.08); }
        }
        @keyframes polish-fade {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// =============================================================================
// ACCOUNT HUB — quick links + reference doc per account
// =============================================================================

function AccountHub({ account, onUpdate }) {
  const [doc, setDoc] = useState(account?.reference_doc || '');
  const [docSaveStatus, setDocSaveStatus] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(account?.account_name || '');
  const [linkingAccountId, setLinkingAccountId] = useState('');
  const [linkError, setLinkError] = useState('');
  const [conflictAccount, setConflictAccount] = useState(null);
  const docTimerRef = React.useRef(null);
  const isManual = account?.account_id?.startsWith('manual-');

  const docSpeech = useSpeechRecognition({
    onComplete: async (rawText) => {
      try {
        const data = await api.polish(rawText, 'general');
        const polished = data.polished || formatTranscript(rawText);
        handleDocChange(doc ? doc + '\n' + polished : polished);
      } catch {
        handleDocChange(doc ? doc + '\n' + formatTranscript(rawText) : formatTranscript(rawText));
      }
    }
  });

  // Sync state when account changes
  React.useEffect(() => {
    setDoc(account?.reference_doc || '');
    setNameVal(account?.account_name || '');
    setEditingName(false);
    setLinkingAccountId('');
    setLinkError('');
    setConflictAccount(null);
  }, [account?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!account) {
    return (
      <div style={{ background: 'white', borderRadius: '12px', padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
        Select an account to view its hub
      </div>
    );
  }

  function handleDocChange(val) {
    setDoc(val);
    setDocSaveStatus('saving');
    if (docTimerRef.current) clearTimeout(docTimerRef.current);
    docTimerRef.current = setTimeout(() => {
      onUpdate('reference_doc', val);
      setDocSaveStatus('saved');
      setTimeout(() => setDocSaveStatus(''), 2000);
    }, 1000);
  }

  const card = {
    background: 'white', borderRadius: '12px',
    padding: '1.5rem', marginBottom: '1.25rem',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    border: '1px solid rgba(0,0,0,0.05)'
  };

  const sectionLabel = {
    margin: '0 0 1rem 0', fontSize: '0.8rem',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    color: '#6b7280', fontWeight: '700'
  };

  function saveName() {
    const trimmed = nameVal.trim();
    if (trimmed && trimmed !== account.account_name) {
      onUpdate('account_name', trimmed);
    }
    setEditingName(false);
  }

  async function handleLinkAccount() {
    if (!linkingAccountId || linkingAccountId.length < 7) return;
    setLinkError('');
    setConflictAccount(null);
    try {
      const res = await api._fetch(`${API_BASE}/accounts/${account.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: linkingAccountId })
      });
      const data = await res.json();
      if (res.status === 409) {
        // Find the conflicting account to offer merge
        const allAccounts = await api.getAccounts();
        const conflict = allAccounts.find(a => a.account_id === linkingAccountId);
        setConflictAccount(conflict);
      } else if (!res.ok) {
        setLinkError(data.error || 'Failed to link account');
      } else {
        onUpdate('_refresh', null);
        setLinkingAccountId('');
      }
    } catch {
      setLinkError('Failed to link account');
    }
  }

  async function handleMergeInto() {
    if (!conflictAccount) return;
    if (!window.confirm(`Merge "${account.account_name}" into "${conflictAccount.account_name}"? All your notes, links, and ideas will be moved over.`)) return;
    try {
      await api.mergeAccounts(account.id, conflictAccount.id);
      onUpdate('_refresh', null);
    } catch {
      setLinkError('Merge failed');
    }
  }

  async function handleDeleteAccount() {
    if (!window.confirm(`Delete "${account.account_name}"? This will remove all entries, links, and notes for this account.`)) return;
    try {
      await api.deleteAccount(account.id);
      onUpdate('_refresh', null);
    } catch {
      setLinkError('Failed to delete account');
    }
  }

  return (
    <div>
      {/* Account Name Header */}
      <div style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        {editingName ? (
          <>
            <input
              autoFocus
              value={nameVal}
              onChange={e => setNameVal(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
              style={{
                flex: 1, fontSize: '1.25rem', fontWeight: '700', color: '#1a1a1a',
                border: 'none', borderBottom: '2px solid #667eea', outline: 'none',
                background: 'transparent', padding: '0.125rem 0'
              }}
            />
            <button onClick={saveName} style={{ background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', padding: '0.3rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}>Save</button>
            <button onClick={() => setEditingName(false)} style={{ background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '6px', padding: '0.3rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer' }}>Cancel</button>
          </>
        ) : (
          <>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#1a1a1a', flex: 1 }}>{account.account_name}</h2>
            <button onClick={() => setEditingName(true)} title="Edit name" style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center' }}>
              <Edit2 size={15} />
            </button>
          </>
        )}
      </div>

      {/* Link to Google Ads — shown for manual/pending accounts */}
      {isManual && (
        <div style={{
          ...card,
          background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
          border: '1px solid #fde68a'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />
            <h3 style={{ ...sectionLabel, margin: 0, color: '#92400e' }}>Pending Google Ads Access</h3>
          </div>
          <p style={{ fontSize: '0.8125rem', color: '#92400e', margin: '0 0 0.75rem', lineHeight: '1.5' }}>
            Once you have access, enter the Google Ads account ID to link this account. All your notes, links, and ideas will be preserved.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input
              value={linkingAccountId}
              onChange={e => { setLinkingAccountId(e.target.value.replace(/\D/g, '')); setLinkError(''); setConflictAccount(null); }}
              placeholder="Google Ads Account ID (e.g. 1234567890)"
              style={{
                flex: 1, padding: '0.5rem 0.75rem',
                border: '1px solid #fde68a', borderRadius: '6px',
                fontSize: '0.8125rem', background: 'white', boxSizing: 'border-box'
              }}
              onKeyDown={e => e.key === 'Enter' && linkingAccountId.length >= 7 && handleLinkAccount()}
            />
            <button
              onClick={handleLinkAccount}
              disabled={linkingAccountId.length < 7}
              style={{
                background: linkingAccountId.length >= 7 ? '#10b981' : '#d1d5db',
                color: 'white', border: 'none',
                padding: '0.5rem 1rem', borderRadius: '6px',
                fontSize: '0.8125rem', fontWeight: '600',
                cursor: linkingAccountId.length >= 7 ? 'pointer' : 'not-allowed'
              }}
            >Link</button>
          </div>
          {/* Conflict — offer merge */}
          {conflictAccount && (
            <div style={{
              marginTop: '0.75rem', padding: '0.75rem',
              background: 'white', borderRadius: '8px',
              border: '1px solid #fde68a'
            }}>
              <p style={{ margin: '0 0 0.5rem', fontSize: '0.8125rem', color: '#92400e', lineHeight: '1.5' }}>
                That account ID belongs to <strong>{conflictAccount.account_name}</strong>. Would you like to merge this account into it? All your notes, links, and ideas will be moved over.
              </p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={handleMergeInto} style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white', border: 'none', padding: '0.4rem 1rem',
                  borderRadius: '6px', fontSize: '0.8125rem', fontWeight: '600', cursor: 'pointer'
                }}>Merge into {conflictAccount.account_name}</button>
                <button onClick={() => setConflictAccount(null)} style={{
                  background: '#f3f4f6', color: '#6b7280', border: 'none',
                  padding: '0.4rem 0.75rem', borderRadius: '6px', fontSize: '0.8125rem', cursor: 'pointer'
                }}>Cancel</button>
              </div>
            </div>
          )}
          {linkError && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#dc2626' }}>{linkError}</div>
          )}
          {/* Delete manual account */}
          <div style={{ marginTop: '0.75rem', borderTop: '1px solid #fde68a', paddingTop: '0.75rem' }}>
            <button onClick={handleDeleteAccount} style={{
              background: 'none', border: 'none', color: '#dc2626',
              fontSize: '0.8rem', cursor: 'pointer', padding: 0, opacity: 0.7
            }}>
              Delete this account
            </button>
          </div>
        </div>
      )}

      {/* Delete account — available for all accounts */}
      {!isManual && (
        <div style={{ marginBottom: '1rem', textAlign: 'right' }}>
          <button onClick={handleDeleteAccount} style={{
            background: 'none', border: 'none', color: '#dc2626',
            fontSize: '0.8rem', cursor: 'pointer', padding: 0, opacity: 0.6
          }}>
            Remove this account
          </button>
        </div>
      )}

      {/* Reference Doc / Notes */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={sectionLabel}>Reference Doc</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {docSaveStatus === 'saving' && <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Saving...</span>}
            {docSaveStatus === 'saved' && <span style={{ fontSize: '0.75rem', color: '#10b981' }}>✓ Saved</span>}
            <button
              onClick={() => docSpeech.isListening ? docSpeech.stop() : docSpeech.start()}
              title={docSpeech.isListening ? 'Stop recording' : 'Dictate into reference doc'}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.25rem',
                padding: '0.25rem 0.7rem', borderRadius: '999px',
                border: docSpeech.isListening ? 'none' : '1px solid #e5e7eb',
                background: docSpeech.isListening ? '#6b7280' : 'white',
                color: docSpeech.isListening ? 'white' : '#9ca3af',
                fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {docSpeech.isListening ? <MicOff size={12} /> : <Mic size={12} />}
              {docSpeech.isListening ? 'Stop' : 'Dictate'}
            </button>
          </div>
        </div>
        <textarea
          value={docSpeech.isListening ? (doc ? doc + ' ' : '') + docSpeech.interimText : doc}
          onChange={e => !docSpeech.isListening && handleDocChange(e.target.value)}
          readOnly={docSpeech.isListening}
          placeholder={`Landing pages, creative briefs, account notes, login details...\n\nAnything you want to reference quickly for ${account.account_name}.`}
          style={{
            width: '100%', minHeight: '320px',
            padding: '1rem', border: '1px solid #e5e7eb',
            borderRadius: '8px', fontSize: '0.9rem',
            fontFamily: 'inherit', lineHeight: '1.7',
            resize: 'vertical', color: '#1a1a1a',
            background: '#fafbfc', boxSizing: 'border-box',
            outline: 'none'
          }}
        />
      </div>
    </div>
  );
}

// Badge color palette keyed by the first word of the two-word action label
function getBadgeColors(actionLabel) {
  const label = (actionLabel || '').toLowerCase();
  if (label.startsWith('ad group')) return { bg: '#ecfeff', text: '#0e7490', border: '#a5f3fc' };
  const first = label.split(' ')[0];
  const palette = {
    keyword:   { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
    campaign:  { bg: '#f5f3ff', text: '#6d28d9', border: '#ddd6fe' },
    budget:    { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0' },
    ad:        { bg: '#fffbeb', text: '#b45309', border: '#fde68a' },
    bid:       { bg: '#eef2ff', text: '#4338ca', border: '#c7d2fe' },
    targeting: { bg: '#fff1f2', text: '#be123c', border: '#fecdd3' },
  };
  return palette[first] || { bg: '#f3f4f6', text: '#374151', border: '#e5e7eb' };
}

// Render a saved note with colored left borders by platform
function renderPlatformNote(note) {
  if (!note || !note.trim()) return null;

  const blocks = [];
  for (const line of note.split('\n')) {
    if (!line.trim()) continue;

    // New heading format: "Google", "Meta", "General", or "Platform > Campaign"
    const headingMatch = line.trim().match(/^(Google Ads|Google|Meta|General)(?:\s*>\s*(.+))?$/);
    if (headingMatch) {
      const platform = (headingMatch[1] === 'Google' || headingMatch[1] === 'Google Ads') ? 'google_ads'
                     : headingMatch[1] === 'Meta' ? 'meta_ads' : 'general';
      const campaign = headingMatch[2] || null;
      blocks.push({ platform, heading: line.trim(), campaign, text: '' });
      continue;
    }

    // Old prefix format: [Google Ads] or [Meta]
    let platform = 'general';
    let text = line;
    if (line.startsWith('[Google Ads]')) {
      platform = 'google_ads';
      text = line.slice('[Google Ads]'.length).trim();
    } else if (line.startsWith('[Meta]')) {
      platform = 'meta_ads';
      text = line.slice('[Meta]'.length).trim();
    }

    const last = blocks[blocks.length - 1];
    if (last && last.platform === platform) {
      last.text = last.text ? last.text + '\n' + text : text;
    } else {
      blocks.push({ platform, heading: null, campaign: null, text });
    }
  }

  if (blocks.length === 0) return null;

  return blocks.map((block, i) => {
    const isGoogle = block.platform === 'google_ads';
    const isMeta = block.platform === 'meta_ads';
    const borderColor = isGoogle ? '#4285F4' : isMeta ? '#7c3aed' : '#e5e7eb';
    return (
      <div key={i} style={{
        borderLeft: `3px solid ${borderColor}`,
        paddingLeft: '0.75rem',
        marginBottom: i < blocks.length - 1 ? '0.875rem' : 0
      }}>
        {block.heading && (
          <div style={{
            fontSize: '0.75rem', fontWeight: '700',
            marginBottom: '0.25rem',
            display: 'flex', alignItems: 'center', gap: '0.25rem',
          }}>
            {isGoogle ? (
              <>
                <GoogleLabel />
                {block.campaign && <span style={{ color: '#5f6368' }}>&gt; {block.campaign}</span>}
              </>
            ) : (
              <>
                <span style={{ color: isMeta ? '#7c3aed' : '#6b7280' }}>
                  {isMeta ? 'Meta' : 'General'}
                </span>
                {block.campaign && <span style={{ color: '#5f6368' }}>&gt; {block.campaign}</span>}
              </>
            )}
          </div>
        )}
        {!block.heading && (isGoogle || isMeta) && (
          <div style={{
            fontSize: '0.7rem', fontWeight: '700',
            marginBottom: '0.2rem',
            display: 'flex', alignItems: 'center', gap: '0.2rem',
            color: isGoogle ? '#4285F4' : '#0866FF'
          }}>
            {isGoogle
              ? <GoogleLabel />
              : 'Meta Ads'}
          </div>
        )}
        {block.text && (
          <div style={{ fontSize: '0.9375rem', lineHeight: '1.6', color: '#1a1a1a', whiteSpace: 'pre-wrap' }}>
            {block.text}
          </div>
        )}
      </div>
    );
  });
}

// =============================================================================
// ACCOUNT LINKS — platform & landing page links panel for journal sidebar
// =============================================================================

function AccountLinks({ account, onUpdate }) {
  const [links, setLinks] = useState(account?.quick_links || []);
  const [newLabel, setNewLabel] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [addingLink, setAddingLink] = useState(null);
  const [editingLinkIdx, setEditingLinkIdx] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editQuickOpen, setEditQuickOpen] = useState(false);

  React.useEffect(() => {
    setLinks(account?.quick_links || []);
    setEditingLinkIdx(null);
    setAddingLink(null);
  }, [account?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!account) return null;

  function saveLinks(updated) {
    setLinks(updated);
    onUpdate('quick_links', updated);
  }

  function addLink() {
    if (!newLabel.trim() || !newUrl.trim()) return;
    const url = newUrl.startsWith('http') ? newUrl : 'https://' + newUrl;
    const linkType = addingLink || 'platform';
    saveLinks([...links, { label: newLabel.trim(), url, type: linkType, quickOpen: linkType === 'platform' }]);
    setNewLabel(''); setNewUrl(''); setAddingLink(null);
  }

  function removeLink(idx) {
    saveLinks(links.filter((_, i) => i !== idx));
    if (editingLinkIdx === idx) setEditingLinkIdx(null);
  }

  function startEditLink(idx) {
    setEditingLinkIdx(idx);
    setEditLabel(links[idx].label);
    setEditUrl(links[idx].url);
    setEditQuickOpen(links[idx].quickOpen || false);
  }

  function saveEditLink() {
    if (editingLinkIdx === null || !editLabel.trim() || !editUrl.trim()) return;
    const url = editUrl.startsWith('http') ? editUrl : 'https://' + editUrl;
    saveLinks(links.map((l, i) => i === editingLinkIdx ? { ...l, label: editLabel.trim(), url, quickOpen: editQuickOpen } : l));
    setEditingLinkIdx(null);
  }

  function moveLink(idx, dir) {
    const target = idx + dir;
    if (target < 0 || target >= links.length) return;
    const updated = [...links];
    [updated[idx], updated[target]] = [updated[target], updated[idx]];
    saveLinks(updated);
    if (editingLinkIdx === idx) setEditingLinkIdx(target);
    else if (editingLinkIdx === target) setEditingLinkIdx(idx);
  }

  const sectionLabel = {
    margin: 0, fontSize: '0.75rem',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    color: '#6b7280', fontWeight: '700'
  };

  const card = {
    background: 'white', borderRadius: '10px',
    padding: '1rem', marginBottom: '0.75rem',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    border: '1px solid rgba(0,0,0,0.05)'
  };

  const platformLinks = links.filter(l => (l.type || 'platform') === 'platform');
  const landingLinks = links.filter(l => l.type === 'landing');

  function renderLinkRow(link, idx, sameType, typeIdx) {
    if (editingLinkIdx === idx) {
      return (
        <div key={idx} style={{
          display: 'flex', gap: '0.375rem', flexWrap: 'wrap',
          padding: '0.5rem', background: '#f9fafb',
          border: '1px solid #667eea', borderRadius: '6px'
        }}>
          <input autoFocus value={editLabel} onChange={e => setEditLabel(e.target.value)} placeholder="Label"
            style={{ flex: '1', minWidth: '80px', padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '0.75rem' }} />
          <input value={editUrl} onChange={e => setEditUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveEditLink()} placeholder="URL"
            style={{ flex: '2', minWidth: '100px', padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '0.75rem' }} />
          {link.type !== 'landing' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.7rem', color: '#6b7280', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={editQuickOpen} onChange={e => setEditQuickOpen(e.target.checked)} style={{ accentColor: '#10b981' }} />
              Quick open
            </label>
          )}
          <button onClick={saveEditLink} style={{ background: '#10b981', color: 'white', border: 'none', padding: '0.35rem 0.625rem', borderRadius: '5px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer' }}>Save</button>
          <button onClick={() => setEditingLinkIdx(null)} style={{ background: '#f3f4f6', color: '#6b7280', border: 'none', padding: '0.35rem 0.5rem', borderRadius: '5px', fontSize: '0.75rem', cursor: 'pointer' }}>Cancel</button>
        </div>
      );
    }
    return (
      <div key={idx} style={{
        display: 'flex', alignItems: 'center', gap: '0.375rem',
        background: '#f9fafb', border: '1px solid #e5e7eb',
        borderRadius: '6px', padding: '0.375rem 0.5rem',
        fontSize: '0.8rem'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.0625rem', flexShrink: 0 }}>
          <button onClick={() => moveLink(idx, -1)} disabled={typeIdx === 0} style={{ background: 'none', border: 'none', cursor: typeIdx === 0 ? 'default' : 'pointer', padding: 0, color: typeIdx === 0 ? '#e5e7eb' : '#9ca3af', display: 'flex' }}><ArrowUp size={10} /></button>
          <button onClick={() => moveLink(idx, 1)} disabled={typeIdx === sameType.length - 1} style={{ background: 'none', border: 'none', cursor: typeIdx === sameType.length - 1 ? 'default' : 'pointer', padding: 0, color: typeIdx === sameType.length - 1 ? '#e5e7eb' : '#9ca3af', display: 'flex' }}><ArrowDown size={10} /></button>
        </div>
        {link.quickOpen && (
          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981', flexShrink: 0 }} title="Quick open" />
        )}
        <a href={link.url} target="_blank" rel="noopener noreferrer"
          style={{ color: '#374151', fontWeight: '600', textDecoration: 'none', flex: 1, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {link.label} ↗
        </a>
        {link.type === 'landing' && (
          <button onClick={() => { navigator.clipboard.writeText(link.url); }} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '0.125rem', display: 'flex', flexShrink: 0 }} title="Copy URL"><Copy size={12} /></button>
        )}
        <button onClick={() => startEditLink(idx)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '0.125rem', display: 'flex', flexShrink: 0 }} title="Edit"><Edit2 size={12} /></button>
        <button onClick={() => removeLink(idx)} style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', fontSize: '0.9rem', lineHeight: 1, padding: '0 0.1rem', flexShrink: 0 }} title="Remove">×</button>
      </div>
    );
  }

  function renderAddForm(type) {
    if (addingLink !== type) return null;
    return (
      <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
        <input autoFocus placeholder={type === 'platform' ? 'Label (e.g. Google Ads)' : 'Page name'} value={newLabel} onChange={e => setNewLabel(e.target.value)}
          style={{ flex: '1', minWidth: '80px', padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '0.75rem' }} />
        <input placeholder="URL" value={newUrl} onChange={e => setNewUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && addLink()}
          style={{ flex: '2', minWidth: '100px', padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '0.75rem' }} />
        <button onClick={addLink} style={{ background: '#10b981', color: 'white', border: 'none', padding: '0.35rem 0.625rem', borderRadius: '5px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer' }}>Save</button>
        <button onClick={() => { setAddingLink(null); setNewLabel(''); setNewUrl(''); }} style={{ background: '#f3f4f6', color: '#6b7280', border: 'none', padding: '0.35rem 0.5rem', borderRadius: '5px', fontSize: '0.75rem', cursor: 'pointer' }}>Cancel</button>
      </div>
    );
  }

  return (
    <>
      {/* Platform Links */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.625rem' }}>
          <h3 style={sectionLabel}>Platform Links</h3>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            {platformLinks.some(l => l.quickOpen) && (
              <button
                onClick={() => { platformLinks.filter(l => l.quickOpen).forEach((l, i) => setTimeout(() => window.open(l.url, '_blank'), i * 300)); }}
                style={{ background: '#f3f4f6', color: '#6b7280', border: 'none', padding: '0.2rem 0.5rem', borderRadius: '5px', fontSize: '0.7rem', fontWeight: '600', cursor: 'pointer' }}
              >Open All</button>
            )}
            <button onClick={() => setAddingLink('platform')}
              style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', padding: '0.2rem 0.5rem', borderRadius: '5px', fontSize: '0.7rem', fontWeight: '600', cursor: 'pointer' }}
            >+ Add</button>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          {platformLinks.map((link) => {
            const idx = links.indexOf(link);
            const typeIdx = platformLinks.indexOf(link);
            return renderLinkRow(link, idx, platformLinks, typeIdx);
          })}
          {platformLinks.length === 0 && addingLink !== 'platform' && (
            <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>Google Ads, Meta, GA4, etc.</p>
          )}
        </div>
        {renderAddForm('platform')}
      </div>

      {/* Landing Page Links */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.625rem' }}>
          <h3 style={sectionLabel}>Landing Page Links</h3>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            {landingLinks.length > 0 && (
              <button
                onClick={() => { landingLinks.forEach((l, i) => setTimeout(() => window.open(l.url, '_blank'), i * 300)); }}
                style={{ background: '#f3f4f6', color: '#6b7280', border: 'none', padding: '0.2rem 0.5rem', borderRadius: '5px', fontSize: '0.7rem', fontWeight: '600', cursor: 'pointer' }}
              >Open All</button>
            )}
            <button onClick={() => setAddingLink('landing')}
              style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', padding: '0.2rem 0.5rem', borderRadius: '5px', fontSize: '0.7rem', fontWeight: '600', cursor: 'pointer' }}
            >+ Add</button>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          {landingLinks.map((link) => {
            const idx = links.indexOf(link);
            const typeIdx = landingLinks.indexOf(link);
            return renderLinkRow(link, idx, landingLinks, typeIdx);
          })}
          {landingLinks.length === 0 && addingLink !== 'landing' && (
            <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>Landing pages, funnels, offer pages...</p>
          )}
        </div>
        {renderAddForm('landing')}
      </div>
    </>
  );
}

// =============================================================================
// TEST IDEAS — to-do list of concepts to test
// =============================================================================

function TestIdeas({ ideas, onUpdate }) {
  const [items, setItems] = useState(ideas || []);
  const [newText, setNewText] = useState('');

  React.useEffect(() => { setItems(ideas || []); }, [ideas]);

  function save(updated) {
    setItems(updated);
    onUpdate('test_ideas', updated);
  }

  function addItem() {
    if (!newText.trim()) return;
    save([...items, { id: Date.now(), text: newText.trim(), done: false }]);
    setNewText('');
  }

  function toggleItem(id) {
    save(items.map(i => i.id === id ? { ...i, done: !i.done } : i));
  }

  function removeItem(id) {
    save(items.filter(i => i.id !== id));
  }

  function moveItem(idx, dir) {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const updated = [...items];
    [updated[idx], updated[target]] = [updated[target], updated[idx]];
    save(updated);
  }

  const pending = items.filter(i => !i.done);
  const completed = items.filter(i => i.done);

  return (
    <div style={{
      background: 'white',
      borderRadius: '10px',
      padding: '1rem',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      border: '1px solid rgba(0,0,0,0.05)'
    }}>
      <h3 style={{
        margin: '0 0 1rem 0', fontSize: '0.8rem',
        textTransform: 'uppercase', letterSpacing: '0.06em',
        color: '#6b7280', fontWeight: '700'
      }}>
        Testing Ideas
      </h3>

      {/* Add new item */}
      <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '1rem' }}>
        <input
          value={newText}
          onChange={e => setNewText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addItem()}
          placeholder="Add an idea..."
          style={{
            flex: 1, padding: '0.5rem 0.625rem',
            border: '1px solid #e5e7eb', borderRadius: '6px',
            fontSize: '0.8125rem', background: '#f9fafb',
            boxSizing: 'border-box'
          }}
        />
        <button
          onClick={addItem}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white', border: 'none',
            borderRadius: '6px', padding: '0.5rem',
            cursor: 'pointer', display: 'flex', alignItems: 'center'
          }}
        >
          <Plus size={14} />
        </button>
      </div>

      {/* Pending items */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        {pending.map((item, idx) => {
          const globalIdx = items.indexOf(item);
          return (
            <div key={item.id} style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.5rem 0.5rem',
              borderRadius: '6px',
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              fontSize: '0.8125rem',
              transition: 'all 0.15s ease'
            }}>
              <button
                onClick={() => toggleItem(item.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#d1d5db', display: 'flex' }}
              >
                <Square size={16} />
              </button>
              <span style={{ flex: 1, color: '#374151', lineHeight: '1.4' }}>{item.text}</span>
              <div style={{ display: 'flex', gap: '0.125rem', flexShrink: 0 }}>
                <button onClick={() => moveItem(globalIdx, -1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.125rem', color: '#d1d5db' }}><ArrowUp size={12} /></button>
                <button onClick={() => moveItem(globalIdx, 1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.125rem', color: '#d1d5db' }}><ArrowDown size={12} /></button>
                <button onClick={() => removeItem(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.125rem', color: '#d1d5db', fontSize: '1rem', lineHeight: 1 }}>×</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Completed items */}
      {completed.length > 0 && (
        <div style={{ marginTop: '0.75rem', borderTop: '1px solid #f3f4f6', paddingTop: '0.75rem' }}>
          <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: '600', marginBottom: '0.375rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Completed ({completed.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {completed.map(item => (
              <div key={item.id} style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.375rem 0.5rem',
                borderRadius: '6px',
                fontSize: '0.8125rem'
              }}>
                <button
                  onClick={() => toggleItem(item.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#10b981', display: 'flex' }}
                >
                  <CheckSquare size={16} />
                </button>
                <span style={{ flex: 1, color: '#9ca3af', textDecoration: 'line-through' }}>{item.text}</span>
                <button onClick={() => removeItem(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.125rem', color: '#d1d5db', fontSize: '1rem', lineHeight: 1 }}>×</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {items.length === 0 && (
        <p style={{ color: '#9ca3af', fontSize: '0.8125rem', margin: 0, textAlign: 'center', padding: '1rem 0' }}>
          Jot down ideas to test — ad copy, audiences, bid strategies...
        </p>
      )}
    </div>
  );
}

// Entry Card Component
function EntryCard({ entry, expandedEntry, setExpandedEntry, onUpdate, onDelete, saveStatus, googleCampaigns, metaCampaigns }) {
  const [note, setNote] = useState(entry.manual_note);
  const [isEditing, setIsEditing] = useState(false);
  const [campaignPicker, setCampaignPicker] = useState(null);

  function entryCampaignsForPlatform(channelId) {
    if (channelId === 'google_ads') return googleCampaigns || [];
    if (channelId === 'meta_ads') return metaCampaigns || [];
    return [];
  }

  const handleNoteChange = (value) => {
    setNote(value);
    onUpdate(entry.id, value);
  };

  function handlePlatformClick(channelId) {
    entryInsertHeading(channelId);
    if (channelId !== 'general' && entryCampaignsForPlatform(channelId).length > 0) {
      setCampaignPicker(prev => prev === channelId ? null : channelId);
    }
  }

  function entryInsertHeading(platform) {
    const label = platform === 'google_ads' ? 'Google'
                : platform === 'meta_ads' ? 'Meta'
                : 'General';

    let separator = '';
    if (note.length > 0) {
      if (!note.endsWith('\n\n')) {
        separator = note.endsWith('\n') ? '\n' : '\n\n';
      }
    }
    const updated = note + separator + label + '\n';
    setNote(updated);
    onUpdate(entry.id, updated);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const len = textareaRef.current.value.length;
        textareaRef.current.selectionStart = len;
        textareaRef.current.selectionEnd = len;
      }
    }, 0);
  }

  function entryRefineCampaign(platform, campaignName) {
    const label = platform === 'google_ads' ? 'Google'
                : platform === 'meta_ads' ? 'Meta'
                : 'General';
    const bare = label + '\n';
    const lastIdx = note.lastIndexOf(bare);
    if (lastIdx === -1) return;
    const updated = note.slice(0, lastIdx) + `${label} > ${campaignName}\n` + note.slice(lastIdx + bare.length);
    setNote(updated);
    onUpdate(entry.id, updated);
    setCampaignPicker(null);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const len = textareaRef.current.value.length;
        textareaRef.current.selectionStart = len;
        textareaRef.current.selectionEnd = len;
      }
    }, 0);
  }

  const isAutoLogged = entry.manual_note.includes('Auto-logged');

  const textareaRef = React.useRef(null);

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '1rem 1.5rem 1.5rem',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      border: '1px solid rgba(0,0,0,0.05)'
    }}>
      {/* Entry Header */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '0.25rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {saveStatus === 'saving' && <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Saving...</span>}
          {saveStatus === 'saved' && <span style={{ fontSize: '0.75rem', color: '#10b981' }}>✓ Saved</span>}
          {isEditing && (
            <button
              onClick={() => {
                setIsEditing(false);
              }}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '0.3rem 0.75rem',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}
            >
              Done
            </button>
          )}
          <button
            onClick={() => setIsEditing(!isEditing)}
            style={{
              background: 'transparent',
              border: 'none',
              color: isEditing ? '#667eea' : '#9ca3af',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            <Edit2 size={16} />
          </button>
          <button
            onClick={() => onDelete(entry.id)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#ef4444',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Manual Note */}
      {isEditing ? (
        <div style={{ marginBottom: '1rem' }}>
          <ColoredNoteEditor
            editorRef={textareaRef}
            value={note}
            onChange={handleNoteChange}
            minHeight="80px"
            border="1px solid #667eea"
          />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', position: 'relative' }}>
            {PLATFORM_CHANNELS.map(ch => (
              <button
                key={ch.id}
                onClick={() => handlePlatformClick(ch.id)}
                title={`Insert ${ch.label} heading`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  padding: '0.35rem 0.75rem',
                  borderRadius: '999px',
                  border: `1px solid ${campaignPicker === ch.id ? ch.color : ch.border}`,
                  background: campaignPicker === ch.id ? ch.bg : 'white',
                  color: ch.color,
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                {ch.id === 'google_ads' ? <GoogleLabel /> : ch.label}
              </button>
            ))}

            {campaignPicker && (
              <CampaignPicker
                platform={campaignPicker}
                campaigns={entryCampaignsForPlatform(campaignPicker)}
                onSelect={(campaignName) => entryRefineCampaign(campaignPicker, campaignName)}
                onClose={() => setCampaignPicker(null)}
              />
            )}
          </div>
          )}
        </div>
      ) : (
        note && !isAutoLogged && (
          <div style={{
            marginBottom: '1rem',
            paddingBottom: '1rem',
            borderBottom: '1px solid #f3f4f6',
            display: 'flex',
            flexDirection: 'column',
            gap: '0'
          }}>
            {renderPlatformNote(note)}
          </div>
        )
      )}

      {/* Auto Changes - Collapsible */}
      {entry.auto_changes && entry.auto_changes.length > 0 && (
        <div>
          <button
            onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
            style={{
              width: '100%',
              background: expandedEntry === entry.id ? '#f9fafb' : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '0.625rem 1rem',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '0.75rem',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', alignItems: 'center' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: '0.25rem' }}>
                Changes
              </span>
              {entry.auto_changes.map((change, idx) => {
                const colors = getBadgeColors(change.type);
                return (
                  <span key={idx} style={{
                    background: colors.bg,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    padding: '0.25rem 0.625rem',
                    borderRadius: '999px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    whiteSpace: 'nowrap'
                  }}>
                    {change.type} · {change.count}
                  </span>
                );
              })}
            </div>
            {expandedEntry === entry.id ? <ChevronDown size={16} style={{ flexShrink: 0, color: '#6b7280' }} /> : <ChevronRight size={16} style={{ flexShrink: 0, color: '#6b7280' }} />}
          </button>

          {/* Expanded Changes */}
          {expandedEntry === entry.id && (
            <div style={{
              marginTop: '0.75rem',
              padding: '1rem',
              background: '#fafbfc',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              {entry.auto_changes.map((change, idx) => {
                const colors = getBadgeColors(change.type);
                return (
                  <div key={idx} style={{ marginBottom: idx < entry.auto_changes.length - 1 ? '1rem' : 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span style={{
                        background: colors.bg,
                        color: colors.text,
                        border: `1px solid ${colors.border}`,
                        padding: '0.2rem 0.6rem',
                        borderRadius: '999px',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                      }}>
                        {change.type}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{change.count} change{change.count !== 1 ? 's' : ''}</span>
                    </div>
                    <div style={{ paddingLeft: '0.5rem' }}>
                      {change.details.slice(0, 5).map((detail, detailIdx) => (
                        <div key={detailIdx} style={{
                          fontSize: '0.8125rem',
                          color: '#6b7280',
                          marginBottom: '0.25rem',
                          fontFamily: 'monospace',
                          background: 'white',
                          padding: '0.375rem 0.625rem',
                          borderRadius: '4px',
                          border: `1px solid ${colors.border}`,
                          borderLeft: `3px solid ${colors.border}`
                        }}>
                          {detail}
                        </div>
                      ))}
                      {change.details.length > 5 && (
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                          +{change.details.length - 5} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
